import { cn } from '../ui/cn';

/**
 * Send-level progress indicator (PRO-0230 §5, D7). Shows IN_PROGRESS vs COMPLETE
 * and a progress bar derived from `state` + `progress`. Accessible: the bar is a
 * `role="progressbar"` with `aria-valuenow/min/max` and a text alternative
 * ("142 of 500 sent"); the state is conveyed by text + icon, NEVER colour alone
 * (NF-10, WCAG 2.2 AA). Counts only — never a recipient name/address/body (SEC-8).
 */
export interface SendProgressIndicatorProps {
  readonly state: 'IN_PROGRESS' | 'COMPLETE';
  readonly progress: number;
  readonly counts: { queued: number; sending: number; sent: number; failed: number };
}

export function SendProgressIndicator({
  state,
  progress,
  counts,
}: SendProgressIndicatorProps): JSX.Element {
  const total = counts.queued + counts.sending + counts.sent + counts.failed;
  const terminal = counts.sent + counts.failed;
  const pct = Math.round(Math.min(1, Math.max(0, progress)) * 100);
  const textAlt = `${terminal} of ${total} processed`;
  const stateLabel = state === 'IN_PROGRESS' ? 'In progress' : 'Complete';
  const stateIcon = state === 'IN_PROGRESS' ? '⏳' : '✓';

  return (
    <div className="my-2">
      <p className="mb-1 mt-0 font-bold">
        <span aria-hidden="true" className="mr-1.5">
          {stateIcon}
        </span>
        {stateLabel} — {textAlt}
      </p>
      <div
        role="progressbar"
        aria-valuenow={pct}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`Send progress: ${textAlt}`}
        className="h-3 w-full max-w-sm overflow-hidden rounded-md border border-border bg-success-bg"
      >
        <div
          aria-hidden="true"
          className={cn(
            'h-full transition-[width] duration-300 ease-ds',
            state === 'COMPLETE' ? 'bg-success' : 'bg-primary',
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}
